// Stage 6A §3 — broadcast slug system. The registry (catalogue surfaced in the
// picker), the pure resolver (template + recipient context → resolved text),
// and the value builders that map the data model into slug values.
//
// Deliberately Vite-free + pure: shared by the client picker (registry) and
// the server slug resolution at /send + preview-content (resolver + builders).
// Slug resolution that needs per-account data (link slugs) runs server-side;
// the client never resolves link slugs because it lacks the account fields.
//
// Token format is {snake_case}. Unknown tokens are left LITERAL (mirrors the
// existing server templating rule) so typos stay discoverable rather than
// vanishing. Known tokens that have no value resolve to '' (contact/static) or
// '[Not configured]' (link slugs) — never literal.

import { UNSUBSCRIBE_SLUG_PLACEHOLDER } from './emailUnsubscribeTag.js';

export const NOT_CONFIGURED = '[Not configured]';

export type SlugCategory = 'static' | 'contact' | 'account';

// 'value' single-value account slugs join with ', ' across multiple accounts;
// 'link' slugs render as a bulleted list with a [Not configured] marker for
// null values (§3.2). Irrelevant for static/contact slugs.
export type SlugRenderKind = 'value' | 'link';

export type SlugDef = {
  token: string;
  label: string;
  category: SlugCategory;
  renderKind: SlugRenderKind;
  description: string;
  example: string;
};

export const SLUG_DEFS: SlugDef[] = [
  // ----- static (same for every recipient) -----
  { token: 'brand_name', label: 'Brand name', category: 'static', renderKind: 'value', description: "The broadcast's Brand account name.", example: 'Snap Fitness Australia' },
  { token: 'sender_name', label: 'Sender name', category: 'static', renderKind: 'value', description: 'Resolved From name for the send.', example: 'Ethan Bailey' },
  { token: 'account_manager_name', label: 'Account Manager Name', category: 'static', renderKind: 'value', description: "The recipient's account manager (falls back to the sender when the account has no manager).", example: 'Ethan Bailey' },
  { token: 'current_date', label: 'Current date', category: 'static', renderKind: 'value', description: "Today's date when the broadcast is sent.", example: '28 May 2026' },
  { token: 'current_year', label: 'Current year', category: 'static', renderKind: 'value', description: 'The current year.', example: '2026' },
  { token: 'current_month', label: 'Current month', category: 'static', renderKind: 'value', description: 'The current month name.', example: 'May' },
  { token: 'unsubscribe_link', label: 'Unsubscribe link', category: 'static', renderKind: 'value', description: "Inserts a link to the recipient's email preferences page, where they can unsubscribe. If you don't add this, a compliance footer is appended automatically.", example: 'Unsubscribe' },

  // ----- per-contact -----
  { token: 'first_name', label: 'First name', category: 'contact', renderKind: 'value', description: "Recipient's first name.", example: 'Cassie' },
  { token: 'last_name', label: 'Last name', category: 'contact', renderKind: 'value', description: "Recipient's last name.", example: 'Jones' },
  { token: 'full_name', label: 'Full name', category: 'contact', renderKind: 'value', description: 'First + last name combined.', example: 'Cassie Jones' },
  { token: 'email', label: 'Email', category: 'contact', renderKind: 'value', description: "Recipient's email address.", example: 'cassie@example.com' },
  { token: 'phone', label: 'Phone', category: 'contact', renderKind: 'value', description: "Recipient's phone number.", example: '0400 000 000' },

  // ----- per-account (single value; comma-joined when multiple) -----
  { token: 'location_name', label: 'Location name', category: 'account', renderKind: 'value', description: 'Account name. Comma-joined for multi-location recipients.', example: 'Snap Fitness Chermside' },
  { token: 'location_suburb', label: 'Location suburb', category: 'account', renderKind: 'value', description: 'Franchise suburb. Comma-joined for multi-location recipients.', example: 'Chermside' },
  { token: 'location_state', label: 'Location state', category: 'account', renderKind: 'value', description: 'State. Comma-joined for multi-location recipients.', example: 'QLD' },

  // ----- per-account (link; bulleted list when multiple) -----
  { token: 'client_google_drive_link', label: 'Client Google Drive', category: 'account', renderKind: 'link', description: "Client's Google Drive folder URL.", example: 'https://drive.google.com/...' },
  { token: 'client_internal_drive_link', label: 'Internal Drive', category: 'account', renderKind: 'link', description: 'Internal Drive folder URL.', example: 'https://drive.google.com/...' },
  { token: 'meta_ad_account_link', label: 'Meta ad account', category: 'account', renderKind: 'link', description: 'Meta (Facebook) ad account URL.', example: 'https://business.facebook.com/...' },
  { token: 'google_ads_link', label: 'Google Ads', category: 'account', renderKind: 'link', description: 'Google Ads account URL.', example: 'https://ads.google.com/...' },
  { token: 'ga4_property_link', label: 'GA4 property', category: 'account', renderKind: 'link', description: 'Google Analytics 4 property report link.', example: 'https://analytics.google.com/...' },
  { token: 'facebook_page_link', label: 'Facebook page', category: 'account', renderKind: 'link', description: 'Facebook page URL.', example: 'https://facebook.com/...' },
  { token: 'instagram_link', label: 'Instagram', category: 'account', renderKind: 'link', description: 'Instagram profile URL.', example: 'https://instagram.com/...' },
  { token: 'tiktok_link', label: 'TikTok', category: 'account', renderKind: 'link', description: 'TikTok profile URL.', example: 'https://tiktok.com/...' },
  { token: 'google_business_link', label: 'Google Business', category: 'account', renderKind: 'link', description: 'Google Business Profile URL.', example: 'https://business.google.com/...' },
  { token: 'website_link', label: 'Website', category: 'account', renderKind: 'link', description: 'Account website URL.', example: 'https://example.com' },
  { token: 'salesforce_link', label: 'Salesforce', category: 'account', renderKind: 'link', description: 'Salesforce account link (not yet configured).', example: NOT_CONFIGURED },
  { token: 'report_link', label: 'Report', category: 'account', renderKind: 'link', description: 'Reporting dashboard URL.', example: 'https://...' },
];

const DEF_BY_TOKEN = new Map(SLUG_DEFS.map((d) => [d.token, d]));

export function getSlugDef(token: string): SlugDef | undefined {
  return DEF_BY_TOKEN.get(token);
}

// One audience account's resolved slug values, keyed by account-category token.
// location_name is always present (Account.name); everything else may be null.
export type AccountSlugValues = Record<string, string | null>;

export type SlugContext = {
  static: Record<string, string>;
  contact: Record<string, string>;
  // Ordered (caller decides order; the orchestrator sorts alphabetically by
  // location_name). One entry per audience-linked account for this recipient.
  accounts: Array<{ values: AccountSlugValues }>;
};

const TOKEN_RE = /\{([a-z0-9_]+)\}/g;

function nonEmpty(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

// Render a single account-link slug across the recipient's accounts (§3.2):
// 0 accounts → ''; 1 → the URL or [Not configured]; 2+ → a markdown/plaintext
// bulleted list, one line per account, labelled by location_name.
function renderLinkSlug(token: string, accounts: SlugContext['accounts']): string {
  if (accounts.length === 0) return '';
  if (accounts.length === 1) {
    const v = accounts[0]!.values[token];
    return nonEmpty(v) ? v : NOT_CONFIGURED;
  }
  return accounts
    .map((a) => {
      const label = a.values['location_name'] ?? '';
      const v = a.values[token];
      return `- ${label}: ${nonEmpty(v) ? v : NOT_CONFIGURED}`;
    })
    .join('\n');
}

// Render a single-value account slug across the recipient's accounts: the
// non-empty values comma-joined (so a null suburb on one of three locations
// doesn't produce "A, , C").
function renderValueSlug(token: string, accounts: SlugContext['accounts']): string {
  return accounts
    .map((a) => a.values[token])
    .filter(nonEmpty)
    .join(', ');
}

export function resolveSlugTemplate(template: string, ctx: SlugContext): string {
  return template.replace(TOKEN_RE, (literal, token: string) => {
    const def = DEF_BY_TOKEN.get(token);
    if (!def) return literal; // unknown token stays literal
    if (def.category === 'static') return ctx.static[token] ?? '';
    if (def.category === 'contact') return ctx.contact[token] ?? '';
    // account
    return def.renderKind === 'link'
      ? renderLinkSlug(token, ctx.accounts)
      : renderValueSlug(token, ctx.accounts);
  });
}

// ---------- referenced-token extraction + unresolved-slug warnings (§3.4) ----------

// The DISTINCT known slug tokens referenced in a template (unknown tokens
// ignored). Used to drive the picker's "in use" hints + the warning panel.
export function extractReferencedTokens(template: string): string[] {
  const out = new Set<string>();
  for (const match of template.matchAll(TOKEN_RE)) {
    const token = match[1]!;
    if (DEF_BY_TOKEN.has(token)) out.add(token);
  }
  return [...out];
}

export type SlugWarning = {
  token: string;
  label: string;
  recipientsAffected: number;
};

// Count, per referenced account LINK slug, how many recipients have at least
// one account missing that value (i.e. would render [Not configured]). Powers
// the compose-page warning panel ("3 recipients have no client drive link
// set"). Only account link slugs can be "unresolved" — static/contact/
// single-value slugs always render something. salesforce_link, deferred to
// null, will therefore flag every recipient when used — intentional, it tells
// the AM it isn't wired yet.
export function computeSlugWarnings(
  content: string,
  // `accountManagerName` is the recipient's RESOLVED account-manager name, or
  // null when no real AM resolved for their first account (the value then falls
  // back to the initiator). When the {account_manager_name} slug is used, those
  // null recipients are flagged so the composer knows their name won't appear.
  recipients: Array<{
    accounts: Array<{ values: AccountSlugValues }>;
    accountManagerName?: string | null;
  }>,
): SlugWarning[] {
  const referenced = extractReferencedTokens(content);
  const linkTokens = referenced.filter((t) => {
    const def = DEF_BY_TOKEN.get(t);
    return def?.category === 'account' && def.renderKind === 'link';
  });

  const warnings: SlugWarning[] = [];
  for (const token of linkTokens) {
    let affected = 0;
    for (const r of recipients) {
      // A recipient with no audience accounts, or any account missing the
      // value, would surface [Not configured] for this slug.
      const missing =
        r.accounts.length === 0 || r.accounts.some((a) => !nonEmpty(a.values[token]));
      if (missing) affected++;
    }
    if (affected > 0) {
      warnings.push({ token, label: DEF_BY_TOKEN.get(token)!.label, recipientsAffected: affected });
    }
  }

  // {account_manager_name} is a per-recipient static slug: flag recipients whose
  // first audience account has no/stale AM (value falls back to the sender).
  if (referenced.includes('account_manager_name')) {
    let affected = 0;
    for (const r of recipients) {
      if (!nonEmpty(r.accountManagerName ?? null)) affected++;
    }
    if (affected > 0) {
      warnings.push({
        token: 'account_manager_name',
        label: DEF_BY_TOKEN.get('account_manager_name')!.label,
        recipientsAffected: affected,
      });
    }
  }

  return warnings;
}

// ---------- value builders (data model → slug values) ----------

// GA4 deep link from the numeric property id (Q2 decision). The 'p' prefix is
// required by the GA4 URL format; null property id → null (renders as
// [Not configured] in link slugs).
export function formatGa4PropertyLink(propertyId: string | null): string | null {
  const id = propertyId?.trim();
  if (!id) return null;
  return `https://analytics.google.com/analytics/web/#/p${id}/reports/intelligenthome`;
}

// Minimal, decoupled source shape so the builder stays pure + testable without
// the full Account / FranchiseLocationProfile types.
export type AccountSlugSource = {
  name: string;
  // Account.billingAddress?.state — preferred for location_state.
  billingState: string | null;
  // FranchiseLocationProfile.franchiseSuburb — location_suburb (the field lives
  // on the profile, not the Account, despite spec §3.1's wording).
  franchiseSuburb: string | null;
  // FranchiseLocationProfile.state — fallback for location_state.
  profileState: string | null;
  clientDriveUrl: string | null;
  internalDriveUrl: string | null;
  fbAdAccountUrl: string | null;
  googleAdsUrl: string | null;
  googleAnalyticsPropertyId: string | null;
  facebookPageUrl: string | null;
  instagramUrl: string | null;
  tiktokUrl: string | null;
  googleBusinessProfileUrl: string | null;
  websiteUrl: string | null;
  reportUrl: string | null;
};

export function buildAccountSlugValues(src: AccountSlugSource): AccountSlugValues {
  return {
    location_name: src.name,
    location_suburb: src.franchiseSuburb,
    location_state: src.billingState ?? src.profileState,
    client_google_drive_link: src.clientDriveUrl,
    client_internal_drive_link: src.internalDriveUrl,
    meta_ad_account_link: src.fbAdAccountUrl,
    google_ads_link: src.googleAdsUrl,
    ga4_property_link: formatGa4PropertyLink(src.googleAnalyticsPropertyId),
    facebook_page_link: src.facebookPageUrl,
    instagram_link: src.instagramUrl,
    tiktok_link: src.tiktokUrl,
    google_business_link: src.googleBusinessProfileUrl,
    website_link: src.websiteUrl,
    // Salesforce link deferred for v1 (no org domain confirmed) — always
    // renders [Not configured]. Wire the template here when the domain lands.
    salesforce_link: null,
    report_link: src.reportUrl,
  };
}

const AU_TZ = 'Australia/Sydney';

export function buildStaticSlugValues(input: {
  brandName: string;
  senderName: string;
  // The recipient's account-manager name (§ account_manager_name slug). Always
  // resolved to a non-empty string by the caller — the recipient's first-account
  // AM, falling back to the initiator's name — so it's a required field here to
  // force every call site to supply it.
  accountManagerName: string;
  now?: Date;
}): Record<string, string> {
  const now = input.now ?? new Date();
  // Format in AU time so "current date" reflects the agency's day regardless
  // of the (UTC) Cloud Run clock.
  return {
    brand_name: input.brandName,
    sender_name: input.senderName,
    account_manager_name: input.accountManagerName,
    // §2.5 — resolves to a markdown-safe PLACEHOLDER (see emailUnsubscribeTag.ts),
    // swapped for SendGrid's ASM PREFERENCES tag after render, which SendGrid fills with the
    // per-recipient preferences-page URL at delivery. Shared with
    // server/lib/unsubscribeFooter.ts via src/lib/emailUnsubscribeTag.ts.
    unsubscribe_link: UNSUBSCRIBE_SLUG_PLACEHOLDER,
    current_date: new Intl.DateTimeFormat('en-AU', {
      timeZone: AU_TZ,
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(now),
    current_year: new Intl.DateTimeFormat('en-AU', { timeZone: AU_TZ, year: 'numeric' }).format(now),
    current_month: new Intl.DateTimeFormat('en-AU', { timeZone: AU_TZ, month: 'long' }).format(now),
  };
}

export function buildContactSlugValues(c: {
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
}): Record<string, string> {
  const full = [c.firstName, c.lastName].filter((p): p is string => nonEmpty(p)).join(' ');
  return {
    first_name: c.firstName ?? '',
    last_name: c.lastName ?? '',
    full_name: full || c.firstName || '',
    email: c.email ?? '',
    phone: c.phone ?? '',
  };
}
